import React, { useMemo } from 'react';
import { FormSpy } from 'react-final-form';
import { Navigate, useOutletContext, useParams } from 'react-router-dom';

import {
  IDENTITY_PROVIDERS,
  inputAcceptTransmitDataConfig,
  inputBirthcountryConfig,
  inputBirthdateConfig,
  inputBirthplaceConfig,
  inputEmailConfig,
  inputEmailValidConfig,
  inputFileUploadConfig,
  inputIdentityProviderConfig,
  inputServiceProviderConfig,
  inputSocialSecurityConfig,
  inputTextareaDescriptionConfig,
} from '../../../configs';
import { PageLayoutContext, StandardFormValues } from '../../../interfaces';
import { personGroupDecorator } from '../../../libs/decorators';
import { FileInputLabel, FormName, FormType } from '../../../libs/enums';
import { getIdpLabelByValue } from '../../../libs/helpers';
import {
  CheckboxComponent,
  FileUploadComponent,
  RadioInputComponent,
  SelectComponent,
  TextAreaComponent,
  TextInputComponent,
} from '../../dsfr';
import { ErrorCodeComponent } from '../error-code';
import { FormWrapperComponent } from '../form-wrapper';
import { PersonGroup } from '../person-group';
import { ReadOnlyComponent } from '../read-only';

interface RouteParams extends Record<string, string | undefined> {
  errorCode: string;
}

export const StandardForm = React.memo(() => {
  const { errorCode } = useParams<RouteParams>();
  const { errorId, idpName, platform, spName } = useOutletContext<PageLayoutContext>();

  const identityProvider = getIdpLabelByValue(idpName);
  const serviceProvider = (!!spName && spName) || undefined;

  const memoizedDescription = useMemo(
    () => (
      <React.Fragment>
        Pour faciliter la prise en charge de votre demande, veuillez nous transmettre la
        notification de connexion reçue dont vous ne seriez pas à l’origine.
        <br />
        <br />
        Taille maximale : 2Mo. Les formats supportés sont : JPG, JPEG, PNG, GIF, PDF, ODT, DOCX.
      </React.Fragment>
    ),
    [],
  );

  if (!errorCode) {
    return <Navigate replace state={{ error: `${FormType.STANDARD}/${errorCode}` }} to="/error" />;
  }

  return (
    <FormWrapperComponent<StandardFormValues>
      decorators={[personGroupDecorator]}
      initialValues={{
        acceptTransmitData: undefined,
        birthcountry: undefined,
        birthdate: undefined,
        birthplace: undefined,
        email: undefined,
        emailValid: undefined,
        errorCode,
        errorId,
        firstName: undefined,
        firstNameDisabled: false,
        formName: FormName.STANDARD,
        formType: FormType.STANDARD,
        identityProvider,
        lastName: undefined,
        lastNameDisabled: false,
        platform,
        serviceProvider,
        socialSecurity: undefined,
        textareaDescription: undefined,
      }}>
      <React.Fragment>
        <div className="fr-grid-row fr-grid-row--gutters fr-mb-3w">
          <div className="fr-col-12 fr-col-sm-6 fr-mb-md-0 fr-mb-2w">
            <ErrorCodeComponent />
          </div>
          <div className="fr-col-12 fr-col-sm-6">
            {identityProvider ? (
              <ReadOnlyComponent {...inputIdentityProviderConfig} />
            ) : (
              <SelectComponent {...inputIdentityProviderConfig} values={IDENTITY_PROVIDERS} />
            )}
          </div>
        </div>
        {serviceProvider ? (
          <ReadOnlyComponent {...inputServiceProviderConfig} />
        ) : (
          <TextInputComponent {...inputServiceProviderConfig} />
        )}
        <PersonGroup />
        <TextInputComponent {...inputBirthdateConfig} />
        <TextInputComponent {...inputBirthcountryConfig} />
        <TextInputComponent {...inputBirthplaceConfig} />
        <TextInputComponent {...inputEmailConfig} />
        <TextInputComponent {...inputEmailValidConfig} />
        <RadioInputComponent {...inputSocialSecurityConfig} />
        <TextAreaComponent {...inputTextareaDescriptionConfig} />
        <FormSpy subscription={{ values: true }}>
          {(props: { values: StandardFormValues }) => {
            const idpService = getIdpLabelByValue(props.values.identityProvider);
            const label = !idpService
              ? FileInputLabel.PJ
              : `${FileInputLabel.PROFIL} ${idpService}`;
            return (
              <FileUploadComponent
                {...inputFileUploadConfig}
                description={memoizedDescription}
                label={label}
              />
            );
          }}
        </FormSpy>
        <CheckboxComponent {...inputAcceptTransmitDataConfig} />
      </React.Fragment>
    </FormWrapperComponent>
  );
});

StandardForm.displayName = 'StandardForm';
